import { DialogType, type IColumn, type IDropdownOption, type IStyle, MessageBarType } from '@fluentui/react';
import {
  BaseDialog,
  ClassNamesFromIStyles,
  Dropdown,
  dropdownStylesInlineLeft,
  IH2OTheme,
  Link,
  linkStylesBlack,
  Loader,
  TextWithCopy,
  textWithCopyStylesBorder,
  useClassNames,
  useHaicPageTitle,
  useTheme,
  useToast,
} from '@h2oai/ui-kit';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import semver from 'semver';

import { workspaceName } from '../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { useUsers } from '../../authz/providers/UsersProvider';
import { FailedToLoadView } from '../../components/FailedToLoadView/FailedToLoadView';
import { NoItemView } from '../../components/NoItemView/NoItemView';
import { RowHeaderTitle } from '../../components/RowHeaderTitle/RowHeaderTitle';
import WidgetList from '../../components/WidgetList/WidgetList';
import { FALLBACK_MLOPS_API_VERSION, useQueryMlopsApiVersion } from '../../discovery-service/discoveryService.hooks';
import { deploymentName } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/deployment_pb';
import { DeploymentStatus_DeploymentState } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/deployment_status_pb';
import { useMLOpsService } from '../../mlops/hooks';
import {
  getDeploymentMetadataQueryOptions,
  getDeploymentStatusQueryOptions,
  useMutationDeleteDeployment,
  useMutationRetryDeployment,
  useQueryDeployments,
  useQueryEndpoints,
} from '../../mlops/mlops.deployer.hooks';
import { useInfiniteQueryFetchAllPages } from '../../services/hooks';
import { useCloudPlatformDiscovery, useEnv } from '../../utils/hooks';
import { formatError } from '../../utils/utils';
import { ContextMenuIconButton } from '../Orchestrator/Workflows';
import { useConfirmDialog } from './ConfirmDialogProvider';
import { ROUTES } from './constants';
import { DeploymentItem, DeploymentStatusWidget, getDeploymentTypeDisplayName } from './Deployments';
import { downloadFileFromGoogleByteStream } from './DeploymentTabDetail';
import { type GranularMLOpsPermissions, useGranularMLOpsPermissions } from './userActions';

interface IDeploymentsStyles {
  showAllDeployments: IStyle;
  noPermissionsInfo: IStyle;
}

const deploymentsStyles = (theme: IH2OTheme): IDeploymentsStyles => {
  return {
    showAllDeployments: {
      display: 'flex',
      justifyContent: 'center',
      padding: '20px 0',
      borderWidth: 1,
      borderTopStyle: 'solid',
      borderColor: theme.semanticColors?.inputBorder,
      borderRadius: 4,
      // TODO: Use theme colors.
      backgroundImage: 'linear-gradient(to top, rgba(255,255,255,0), rgba(255,255,255, 1) 90%)',
      '&:hover': {
        borderColor: theme.semanticColors?.buttonPrimaryBackground,
        cursor: 'pointer',
        button: {
          textDecoration: 'none',
        },
        'button:active': {
          textDecoration: 'none',
        },
      },
      span: {
        color: theme.semanticColors?.textSecondary,
      },
      '&:hover span': {
        color: theme.semanticColors?.textPrimary,
      },
    },
    noPermissionsInfo: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      color: theme.semanticColors?.textSecondary,
    },
  };
};

const useWidgetColumns = ({
  onDeleteDeployment,
  onDownloadLogs,
  onEdit,
  onQuickScore,
  onRetryDeployment,
  permissions,
  isMlopsApiV1_0_x,
}: {
  onDeleteDeployment: (deploymentName: string) => void;
  onDownloadLogs: (deploymentName: string) => void;
  onEdit: (deploymentName: string) => void;
  onQuickScore: (deploymentName: string) => void;
  onRetryDeployment: (deploymentName: string) => void;
  permissions: GranularMLOpsPermissions;
  isMlopsApiV1_0_x: boolean;
}): IColumn[] => [
  {
    key: 'title',
    name: 'Title',
    // TODO: Fix styling.
    // fieldName: 'description',
    minWidth: 200,
    data: {
      headerFieldName: 'displayName',
      listCellProps: {
        onRenderHeader: ({ displayName, name, status, id, workspaceId }: DeploymentItem) => (
          <>
            {RowHeaderTitle({
              title: displayName,
              onClick:
                permissions.isAbleToViewDeployment || permissions.isAbleToUpdateDeployments
                  ? () => onEdit(name)
                  : undefined,
            })}
            {permissions.isAbleToViewDeploymentStatus ? (
              <div
                style={{
                  marginTop: 4,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <DeploymentStatusWidget deploymentId={id} workspaceId={workspaceId} initialStatus={status} />
              </div>
            ) : null}
          </>
        ),
      },
    },
  },
  {
    key: 'type',
    name: 'Type',
    fieldName: 'type',
    minWidth: 120,
    maxWidth: 120,
  },
  {
    key: 'buttons',
    name: '',
    minWidth: 130,
    data: {
      listCellProps: {
        emptyMessage: '',
        onRenderText: ({ name, status }: DeploymentItem) => (
          // TODO: Use theme prop for colors.
          <ContextMenuIconButton
            items={[
              ...(permissions.isAbleToViewDeployment || permissions.isAbleToUpdateDeployments
                ? [
                    {
                      key: permissions.isAbleToUpdateDeployments ? 'edit' : 'view',
                      text: permissions.isAbleToUpdateDeployments ? 'Edit' : 'View',
                      onClick: () => onEdit(name),
                      iconProps: {
                        iconName: permissions.isAbleToUpdateDeployments ? 'Edit' : 'RedEye',
                        style: { color: 'var(--h2o-gray900)' },
                      },
                    },
                  ]
                : []),
              ...(permissions.isAbleToViewDeployment && !!onDownloadLogs
                ? [
                    {
                      key: 'downloadLogs',
                      text: 'Download logs',
                      onClick: () => onDownloadLogs(name),
                      style: {
                        color: 'var(--h2o-gray900)',
                      },
                      iconProps: { iconName: 'DownloadDocument', style: { color: 'var(--h2o-gray900)' } },
                    },
                  ]
                : []),
              ...(permissions.isAbleToQuickScoreDeployment && status.state === DeploymentStatus_DeploymentState.HEALTHY
                ? [
                    {
                      key: 'quickScore',
                      text: 'Quick score',
                      onClick: () => onQuickScore(name),
                      style: {
                        color: 'var(--h2o-gray900)',
                      },
                      iconProps: { iconName: 'TableComputed', style: { color: 'var(--h2o-gray900)' } },
                    },
                  ]
                : []),
              ...(permissions.canRetryDeployments && status.state === DeploymentStatus_DeploymentState.FAILED
                ? [
                    {
                      key: 'retryDeployment',
                      text: 'Retry deployment',
                      onClick: () => onRetryDeployment(name),
                      style: {
                        color: 'var(--h2o-gray900)',
                      },
                      iconProps: { iconName: 'Refresh', style: { color: 'var(--h2o-gray900)' } },
                    },
                  ]
                : []),
              ...(permissions.isAbleToDeleteDeployments
                ? [
                    {
                      key: 'deleteDeployment',
                      text: 'Delete deployment',
                      disabled: isMlopsApiV1_0_x
                        ? !(
                            status?.state === DeploymentStatus_DeploymentState.HEALTHY ||
                            status?.state === DeploymentStatus_DeploymentState.FAILED ||
                            status?.state === DeploymentStatus_DeploymentState.UNHEALTHY ||
                            status?.state === DeploymentStatus_DeploymentState.STOPPED
                          )
                        : !status.updatesAllowed,
                      onClick: () => onDeleteDeployment(name),
                      style: { color: 'var(--h2o-red400)' },
                      iconProps: { iconName: 'Delete', style: { color: 'var(--h2o-red400)' } },
                    },
                  ]
                : []),
            ]}
          />
        ),
        styles: {
          root: {
            display: 'flex',
            flexGrow: 1,
            justifyContent: 'end',
            marginRight: 9,
          },
        },
      },
    },
  },
];

const EndpointOptionsCopy = ({
  endpointOptions,
  defaultSelectedKey,
}: {
  endpointOptions: IDropdownOption[];
  defaultSelectedKey?: string;
}) => {
  const [internalSelectedKey, setInternalSelectedKey] = React.useState<string | undefined>();

  const effectiveKey = internalSelectedKey === undefined ? defaultSelectedKey : internalSelectedKey;

  React.useEffect(() => {
    setInternalSelectedKey(undefined);
  }, [defaultSelectedKey]);

  return (
    <TextWithCopy
      hasBorder
      header={
        <Dropdown
          options={endpointOptions}
          styles={dropdownStylesInlineLeft}
          onChange={(_, option) => setInternalSelectedKey(option?.key as string)}
          selectedKey={effectiveKey}
        />
      }
      styles={[
        textWithCopyStylesBorder,
        {
          header: {
            padding: 0,
            minWidth: 200,
            border: 0,
            borderRadius: 'none',
          },
          root: {
            width: '100%',
          },
        },
      ]}
      text={effectiveKey || 'No endpoint'}
    />
  );
};

const DeploymentsWidget = () => {
  const params = useParams<{ workspace_id: string }>();
  const workspaceFQDN = React.useMemo(
    () => workspaceName.compile({ workspace: params.workspace_id }),
    [params.workspace_id]
  );
  const history = useHistory();
  const { addToast } = useToast();
  const permissions = useGranularMLOpsPermissions(workspaceFQDN);
  const mlopsApiVersionQuery = useQueryMlopsApiVersion();
  const mlopsApiVersion = mlopsApiVersionQuery.data || FALLBACK_MLOPS_API_VERSION;
  const isMlopsApiV1_0_x = semver.satisfies(mlopsApiVersion, '1.0.x');

  const deploymentsQuery = useQueryDeployments(
    {
      pageSize: 10,
      workspaceId: params.workspace_id,
      orderBy: 'create_time desc',
    },
    {
      enabled: permissions.canListDeployments,
      meta: {
        toastError: (err: string) => `Failed to fetch deployments: ${formatError(err)}`,
      },
      select: (data) => {
        const flattenedDeployments = data.pages.flatMap((page) =>
          !page.deployments
            ? []
            : page.deployments.map((d) => {
                const { deployment: deploymentId, workspace: workspaceId } = deploymentName.parse(d.name);
                return { ...d, id: deploymentId, workspaceId };
              })
        );
        return { pages: flattenedDeployments, pageParams: data.pageParams };
      },
    }
  );
  const deployments = deploymentsQuery.data?.pages;
  const { usersNameMap } = useUsers();
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  const deploymentStatusesQuery = !deployments
    ? []
    : deployments.map((d) =>
        getDeploymentStatusQueryOptions({
          deploymentId: d.id,
          mlopsApiUrl,
          queryClient,
          workspaceId: d.workspaceId,
        })
      );
  const deploymentStatuses = useQueries({ queries: deploymentStatusesQuery }).map((query) => query.data);
  const deploymentMetadataQueryOptions = !deployments
    ? []
    : deployments.map((d) =>
        getDeploymentMetadataQueryOptions({
          deploymentId: d.id,
          mlopsApiUrl,
          queryClient,
          workspaceId: d.workspaceId,
        })
      );
  const deploymentMetadataQueries = useQueries({ queries: deploymentMetadataQueryOptions });
  const deploymentMetadata = deploymentMetadataQueries.map((query) => query.data);
  const endpointsQuery = useQueryEndpoints(
    {
      workspaceId: params.workspace_id,
      pageSize: 999,
    },
    {
      enabled: Boolean(params.workspace_id && permissions?.isAbleToViewEndpoints),
      meta: {
        toastError: (err: unknown) => `Failed to fetch endpoints: ${formatError(err)}`,
      },
      select: (data) => {
        const flattenedEndpoints = data.pages.flatMap((page) => page.endpoints || []);
        return { pages: flattenedEndpoints, pageParams: data.pageParams };
      },
    }
  );
  useInfiniteQueryFetchAllPages(endpointsQuery);
  const endpoints = endpointsQuery.data?.pages;
  const deploymentItems = React.useMemo<DeploymentItem[] | undefined>(() => {
    // @ts-expect-error Object.groupBy is not available in TypeScript < 5.2
    const endpointsByDeploymentName = !endpoints ? {} : Object.groupBy(endpoints, (e) => e.target);
    return deployments?.map((deployment, idx) => {
      const isLoading = deploymentMetadataQueries.some((query) => query.isLoading) || endpointsQuery.isLoading;
      const status = deploymentStatuses[idx];
      const metadata = deploymentMetadata[idx];
      const endpoint = metadata?.scoringInterface?.score;
      const endpointsForDeployment = endpointsByDeploymentName[deployment.name];
      const { deployment: deploymentId } = deploymentName.parse(deployment.name);
      const defaultKey = endpoint?.uri;
      const [urlPrefix, urlSuffix] = defaultKey?.includes(deploymentId) ? defaultKey.split(deploymentId, 2) : ['', ''];
      const endpointOptions: IDropdownOption[] = [];
      if (defaultKey) {
        endpointOptions.push({ key: defaultKey, text: 'Scorer (default)' });
      }
      if (endpointsForDeployment) {
        endpointOptions.push(
          // TODO: Replace "any" type.
          ...endpointsForDeployment.map((endpoint: any) => ({
            key: `${urlPrefix}${endpoint.path}${urlSuffix}`,
            text: endpoint.displayName || `${urlPrefix}${endpoint.path}${urlSuffix}`,
          }))
        );
      }

      return {
        ...deployment,
        createdByName: deployment.creator ? usersNameMap?.[deployment?.creator]?.displayName || deployment.creator : '',
        status: status || { state: DeploymentStatus_DeploymentState.DEPLOYMENT_STATE_UNSPECIFIED },
        type: getDeploymentTypeDisplayName(deployment),
        contents: (
          <div
            style={{
              marginLeft: 0,
              marginBottom: 16,
            }}
          >
            <h4 style={{ margin: 0, marginBottom: 6 }}>Endpoints</h4>
            {endpointOptions.length ? (
              isLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', height: 32 }}>
                  <Loader />
                </div>
              ) : (
                <EndpointOptionsCopy
                  endpointOptions={endpointOptions}
                  defaultSelectedKey={defaultKey || (endpointOptions[0]?.key as string)}
                />
              )
            ) : (
              <div style={{ minHeight: 32, display: 'flex', alignContent: 'center', flexWrap: 'wrap' }}>
                No endpoints available
              </div>
            )}
          </div>
        ),
      };
    });
  }, [deployments, deploymentMetadata, deploymentStatuses, endpoints, usersNameMap]);

  const deleteDeploymentMutation = useMutationDeleteDeployment({
    onError: (err) => {
      const message = `Failed to delete deployment: ${formatError(err)}`;
      console.error(message);
      addToast({
        messageBarType: MessageBarType.error,
        message,
      });
    },
  });

  const retryDeploymentMutation = useMutationRetryDeployment({
    onError: (err) => {
      addToast({
        messageBarType: MessageBarType.error,
        message: `Failed to retry deployment: ${formatError(err)}`,
      });
    },
  });

  const loading = deploymentsQuery.isLoading;

  const theme = useTheme(),
    env = useEnv(),
    classNames = useClassNames<IDeploymentsStyles, ClassNamesFromIStyles<IDeploymentsStyles>>(
      'deploymentsStyles',
      deploymentsStyles(theme)
    ),
    { showDialog } = useConfirmDialog(),
    mlopsService = useMLOpsService(),
    [isDialogVisible, setIsDialogVisible] = React.useState(false),
    dialogTimeoutRef = React.useRef<NodeJS.Timeout | null>(null),
    generateDeploymentLogs = async (deploymentId: string) => {
      try {
        const response = await mlopsService.generateDeploymentLogs({
          name: deploymentId,
        });
        const logZipArtifactId = response?.artifactId;
        if (!logZipArtifactId) console.error('No log entries found in the response.');
        return logZipArtifactId;
      } catch (err) {
        const message = `Failed to fetch log entries: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
        return undefined;
      }
    },
    onDismissDialog = () => {
      setIsDialogVisible(false);
      if (dialogTimeoutRef.current) {
        clearTimeout(dialogTimeoutRef.current);
        dialogTimeoutRef.current = null;
      }
    },
    onDownloadLogs = React.useCallback(
      async (deploymentName: string) => {
        if (!deploymentName) return;
        try {
          dialogTimeoutRef.current = setTimeout(() => {
            setIsDialogVisible(true);
            dialogTimeoutRef.current = null;
          }, 1500);
          const logZipArtifactId = await generateDeploymentLogs(deploymentName);
          if (!logZipArtifactId) return;
          const fileName = `deployment-${deploymentName.replace('/', '_')}-logs.zip`;
          await downloadFileFromGoogleByteStream(logZipArtifactId, mlopsApiUrl, fileName);
        } catch (err) {
          const message = `Failed to download logs: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        } finally {
          onDismissDialog();
        }
      },
      [mlopsApiUrl]
    ),
    widgetColumns = useWidgetColumns({
      onDeleteDeployment: (deploymentName) => {
        showDialog?.({
          title: 'Delete deployment?',
          onConfirm: () => {
            void deleteDeploymentMutation.mutate({ name: deploymentName });
          },
        });
      },
      onDownloadLogs: (deploymentName) => onDownloadLogs(deploymentName),
      onEdit: (deploymentName) => history.push(`/mlops/${deploymentName}`),
      onQuickScore: (deploymentName) => history.push(`/mlops/${deploymentName}/scoring`),
      onRetryDeployment: (deploymentName) => {
        retryDeploymentMutation.mutate({
          name: deploymentName,
        });
      },
      permissions,
      isMlopsApiV1_0_x: Boolean(isMlopsApiV1_0_x),
    }),
    widgetListProps = {
      columns: widgetColumns,
      items: deploymentItems?.slice(0, 5),
      loading,
      delayLoader: false,
      onRefresh: deploymentsQuery.refetch,
      NoItemsContent: NoItemView({
        title: 'No deployments',
        description: 'There are no deployments available in this workspace. Deploy your models first.',
        shadowed: false,
      }),
      ErrorContent: FailedToLoadView({
        title: 'Failed to load deployments',
        description: 'Please try again later. If the problem persists, contact our support.',
        actionTitle: 'Retry',
        onActionClick: deploymentsQuery.refetch,
        actionIcon: 'Refresh',
      }),
      compact: true,
    },
    onShowAllDeployments = () => history.push(`/mlops/${workspaceFQDN}${ROUTES.DEPLOYMENTS}`);

  useHaicPageTitle('Deployments | MLOps', env?.cloudInstanceName);

  return (
    <>
      <BaseDialog
        hidden={!isDialogVisible}
        dialogContentProps={{
          type: DialogType.close,
        }}
        modalProps={
          {
            // HACK: Pass empty modalProps, otherwise download button won't open system file save dialog.
          }
        }
        content={
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            <p>Preparing log files, please wait...</p>
          </div>
        }
        onDismiss={onDismissDialog}
      />
      <h3>Recent deployments</h3>
      {/* <div className={classNames.deploymentsWidget}> */}
      <WidgetList {...widgetListProps} />
      {/* </div> */}
      <div className={classNames.showAllDeployments} onClick={onShowAllDeployments}>
        <Link styles={linkStylesBlack}>Show all deployments</Link>
      </div>
    </>
  );
};

export default DeploymentsWidget;
